import { normalizeSeed } from '~/boggle/engine/core'
import { getBoggleChallengeBoardDistributionDefinition } from '~/boggle/config/boardDistributions/boardDistributionRegistry'
import type { BoggleChallengeBoardDistributionDefinition } from '~/boggle/config/boardDistributions/boardDistributionDefinition'
import type { BoggleChallengeBoardDistributionFamilyId } from '~/boggle/config/boardDistributions/types'
import type {
  BoggleChallengeRolledCoreState,
  BoggleResolvedChallengeCore,
} from '~/boggle/config/challenges/coreTypes'
import {
  getBoggleChallengeConfigurationDefinition,
  getBoggleChallengeConfigurationDefinitionsForMode,
  type BoggleChallengeConfigurationId,
} from '~/boggle/config/challenges/registry'
import type {
  BoggleChallengeConfigurationDefinition,
  BoggleChallengeConfigurationRolledState,
  BoggleChallengeConfigurationStepContext,
  BoggleChallengeWeightedStepOption,
} from '~/boggle/config/challenges/types'
import { getBoggleChallengePathModeAdjacency } from '~/boggle/config/challengePathMode'
import type { BogglePlaygroundSettings } from '~/boggle/config/playground'
import type { BoggleChallengePathMode } from '~/boggle/config/challengePathMode'
import type {
  BoggleResolvedChallengeBoardDistribution,
  BoggleResolvedChallengeBonus,
  BoggleResolvedChallengeConfig,
  BoggleResolvedChallengeMod,
} from '~/boggle/config/challenges/modTypes'
import type { BoggleChallengeBonusDefinition } from '~/boggle/config/bonuses/bonusDefinition'
import { getBoggleChallengeBonusDefinition } from '~/boggle/config/bonuses/registry'
import type { BoggleChallengeBonusInstance } from '~/boggle/config/bonuses/types'
import type { BoggleChallengeSpecialTileRevealEntry } from '~/boggle/config/specialTiles/challengeSpecialTileTypes'
import { pickWeightedEntry } from '~/boggle/lib/challenge/random'
import { areBoggleResolvedChallengeModsCompatible } from '~/boggle/lib/challenge/resolvedModOps/conflicts'
import { buildBoggleResolvedChallengeGameplay } from '~/boggle/lib/challenge/resolvedModOps/resolveGameplay'
import {
  createSelectedModsBonusResolverContext,
  resolveModularBoggleChallengeBoardDistributionFamily,
  resolveModularBoggleChallengeBonusFamily,
} from '~/boggle/lib/challenge/resolveModFamily'
import { rollBoggleChallengeSpecialTiles } from '~/boggle/lib/challenge/specialTileRoll'
import { assertValidBoggleChallengeVowelRatioBand } from './vowelRatioBand'
import { normalizeBogglePlaygroundSettings } from '~/boggle/lib/playground'
import {
  getBoggleGameModeDefinition,
  type BoggleChallengeCoreDefinition,
  type BoggleChallengeCoreDefinitionId,
  type BoggleChallengeCoreValue,
  type BoggleGameModeId,
} from '~/boggle/gameModes'

export type BoggleResolvedChallengeRun = BoggleResolvedChallengeConfig & {
  aceScore: number
  configurationId: BoggleChallengeConfigurationId | null
  cores: readonly BoggleResolvedChallengeCore[]
  pathMode: BoggleChallengePathMode
  specialTileReveals: readonly BoggleChallengeSpecialTileRevealEntry[]
  settings: BogglePlaygroundSettings
  targetScore: number
}

export type BoggleChallengeResolvedFamilyDefinition =
  | BoggleChallengeBoardDistributionDefinition
  | BoggleChallengeBonusDefinition

export function toChallengeRollSeed(gameModeId: BoggleGameModeId, seed: number | string) {
  return normalizeSeed(`${gameModeId}:${normalizeSeed(seed)}:challenge-roll`)
}

export function getBoggleChallengePracticeSeed(
  gameModeId: BoggleGameModeId,
  seed: number | string,
) {
  const challengeSeed = normalizeSeed(seed)
  const practiceSeed = normalizeSeed(
    `${gameModeId}:${challengeSeed}:challenge-practice-board`,
  )

  if (practiceSeed !== challengeSeed) {
    return practiceSeed
  }

  return (challengeSeed ^ 0x9e3779b9) >>> 0
}

export function buildResolvedChallengeCore<T extends BoggleChallengeCoreValue>(
  definition: BoggleChallengeCoreDefinition<T>,
  value: T,
): BoggleResolvedChallengeCore {
  const info = definition.getInfo(value)

  return {
    id: definition.id,
    isRow: definition.isRow,
    label: definition.label,
    optionId: info?.id ?? `custom-${String(value)}`,
    tierLabel: info?.tierLabel ?? 'Custom',
    value,
    visual: definition.renderValue(value),
  }
}

export function getBoggleChallengeCoreDefinition(
  gameModeId: BoggleGameModeId,
  coreDefinitionId: BoggleChallengeCoreDefinitionId,
) {
  const definition = getBoggleGameModeDefinition(gameModeId).challenge.coreDefinitions.find(
    current => current.id === coreDefinitionId,
  )

  if (!definition) {
    throw new Error(`No challenge core definition authored for ${gameModeId}:${coreDefinitionId}`)
  }

  return definition as BoggleChallengeCoreDefinition<BoggleChallengeCoreValue>
}

export function resolveBoggleChallengeCoreValue(
  gameModeId: BoggleGameModeId,
  coreDefinitionId: BoggleChallengeCoreDefinitionId,
  value: BoggleChallengeCoreValue,
  settings: BogglePlaygroundSettings,
) {
  const definition = getBoggleChallengeCoreDefinition(gameModeId, coreDefinitionId)

  return {
    core: buildResolvedChallengeCore(definition, value),
    settings: normalizeBogglePlaygroundSettings(definition.createPatch(value), settings),
  }
}

export function buildResolvedChallengeCores(
  gameModeId: BoggleGameModeId,
  settings: BogglePlaygroundSettings,
) {
  return getBoggleGameModeDefinition(gameModeId).challenge.coreDefinitions.map(
    definition =>
      buildResolvedChallengeCore(
        definition as BoggleChallengeCoreDefinition,
        definition.readValue(settings),
      ),
  )
}

export function resolveBoggleChallengeFamily(
  family: BoggleChallengeResolvedFamilyDefinition,
  rngState: number,
  selectedMods: readonly BoggleResolvedChallengeMod[],
) {
  const resolvedBoardDistributionFamily =
    resolveModularBoggleChallengeBoardDistributionFamily(
      family as BoggleChallengeBoardDistributionDefinition,
      rngState,
    )
  if (!resolvedBoardDistributionFamily) {
    return null
  }

  if (
    resolvedBoardDistributionFamily.compatibilityMode === 'selected-mods' &&
    !selectedMods.every(selectedMod =>
      areBoggleResolvedChallengeModsCompatible(selectedMod, resolvedBoardDistributionFamily.mod),
    )
  ) {
    return null
  }

  return resolvedBoardDistributionFamily
}

export function resolveBoggleChallengeBoardDistributionFromRngState(
  options: readonly BoggleChallengeWeightedStepOption<BoggleChallengeBoardDistributionFamilyId | null>[],
  rngState: number,
  selectedMods: readonly BoggleResolvedChallengeMod[],
):
  | { mod: BoggleResolvedChallengeBoardDistribution; rngState: number }
  | { mod: null; rngState: number }
  | null {
  const remainingOptions = [...options]
  let currentRngState = rngState

  while (remainingOptions.length > 0) {
    const pickedOption = pickWeightedEntry(remainingOptions, currentRngState)
    if (pickedOption === null) {
      return null
    }

    if (pickedOption.entry.value === null) {
      return { mod: null, rngState: pickedOption.rngState }
    }

    const family = getBoggleChallengeBoardDistributionDefinition(pickedOption.entry.value)

    const resolvedFamily = resolveBoggleChallengeFamily(
      family,
      pickedOption.rngState,
      selectedMods,
    )

    if (resolvedFamily) {
      return resolvedFamily as {
        mod: BoggleResolvedChallengeBoardDistribution
        rngState: number
      }
    }

    currentRngState = pickedOption.rngState
    const pickedIndex = remainingOptions.findIndex(
      entry => entry.value === pickedOption.entry.value,
    )
    remainingOptions.splice(pickedIndex, 1)
  }

  return null
}

function collectResolvedChallengeModifiers(
  challenge: Pick<BoggleResolvedChallengeConfig, 'boardDistribution' | 'bonuses'>,
) {
  return challenge.boardDistribution
    ? [challenge.boardDistribution, ...challenge.bonuses]
    : [...challenge.bonuses]
}

function applyBoggleResolvedChallengeEntryEffects(
  settings: BogglePlaygroundSettings,
  entry: BoggleResolvedChallengeMod,
) {
  let nextSettings = settings

  for (const effect of entry.effects) {
    if (effect.kind !== 'playground-settings') {
      continue
    }

    nextSettings = normalizeBogglePlaygroundSettings(effect.patch, nextSettings)
  }

  return nextSettings
}

export function applyResolvedChallengeEffects(
  corePreset: BogglePlaygroundSettings,
  challenge: Pick<BoggleResolvedChallengeConfig, 'boardDistribution' | 'bonuses'>,
) {
  return collectResolvedChallengeModifiers(challenge).reduce(
    (settings, entry) => applyBoggleResolvedChallengeEntryEffects(settings, entry),
    corePreset,
  )
}

export function applyResolvedChallengeRunEffects(
  rolledCoreState: BoggleChallengeRolledCoreState,
  challenge: Pick<BoggleResolvedChallengeConfig, 'boardDistribution' | 'bonuses'>,
) {
  return applyResolvedChallengeEffects(rolledCoreState.settings, challenge)
}

function resolveBoggleChallengeBonusSuiteFromRngState(
  options: readonly BoggleChallengeWeightedStepOption<readonly BoggleChallengeBonusInstance[] | null>[],
  rngState: number,
  selectedMods: readonly BoggleResolvedChallengeMod[],
) {
  const remainingOptions = [...options]
  let currentRngState = rngState

  while (remainingOptions.length > 0) {
    const pickedOption = pickWeightedEntry(remainingOptions, currentRngState)
    if (pickedOption === null) {
      return null
    }

    if (pickedOption.entry.value === null) {
      return {
        bonuses: [] as readonly BoggleResolvedChallengeBonus[],
        rngState: pickedOption.rngState,
      }
    }

    const bonusInstances = pickedOption.entry.value
    const uniqueBonusIds = new Set(bonusInstances.map(instance => instance.bonusId))
    if (uniqueBonusIds.size === bonusInstances.length) {
      const resolvedBonuses: BoggleResolvedChallengeBonus[] = []
      let bonusRngState = pickedOption.rngState
      let compatible = true

      for (const bonusInstance of bonusInstances) {
        const selected = [
          ...selectedMods,
          ...resolvedBonuses,
        ]
        const resolvedBonus = resolveModularBoggleChallengeBonusFamily(
          getBoggleChallengeBonusDefinition(bonusInstance.bonusId),
          bonusRngState,
          createSelectedModsBonusResolverContext(selected),
          bonusInstance.value,
        )

        if (
          !resolvedBonus ||
          !selected.every(selectedMod =>
            areBoggleResolvedChallengeModsCompatible(selectedMod, resolvedBonus.mod),
          )
        ) {
          compatible = false
          break
        }

        resolvedBonuses.push(resolvedBonus.mod)
        bonusRngState = resolvedBonus.rngState
      }

      if (compatible) {
        return {
          bonuses: resolvedBonuses,
          rngState: bonusRngState,
        }
      }
    }

    currentRngState = pickedOption.rngState
    const pickedIndex = remainingOptions.indexOf(pickedOption.entry)
    remainingOptions.splice(pickedIndex, 1)
  }

  return null
}

export function resolveBoggleChallengeConfiguredSeedState(
  gameModeId: BoggleGameModeId,
  seed: number | string,
) {
  const eligibleConfigurations = getBoggleChallengeConfigurationDefinitionsForMode(gameModeId)
  if (eligibleConfigurations.length === 0) {
    return null
  }

  const pickedConfiguration = pickWeightedEntry(
    eligibleConfigurations,
    toChallengeRollSeed(gameModeId, seed),
  )
  if (pickedConfiguration === null) {
    throw new Error(`No weighted challenge configurations available for ${gameModeId}`)
  }

  return resolveBoggleChallengeSeedStateForConfiguration(
    gameModeId,
    pickedConfiguration.entry,
    pickedConfiguration.rngState,
  )
}

export function resolveBoggleChallengeConfigurationSeedState(
  gameModeId: BoggleGameModeId,
  configurationId: BoggleChallengeConfigurationId,
  seed: number | string,
) {
  const configuration = getBoggleChallengeConfigurationDefinition(configurationId)

  if (!configuration.gameModeIds.some(current => current === gameModeId)) {
    return null
  }

  return resolveBoggleChallengeSeedStateForConfiguration(
    gameModeId,
    configuration,
    toChallengeRollSeed(gameModeId, `${configuration.id}:${seed}`),
  )
}

function resolveBoggleChallengeSeedStateForConfiguration(
  gameModeId: BoggleGameModeId,
  configuration: BoggleChallengeConfigurationDefinition<BoggleChallengeConfigurationId>,
  initialRngState: number,
) {
  const rolledState: BoggleChallengeConfigurationRolledState = {
    bonuses: [],
    boardDistribution: null,
    coreValuesByDefinitionId: {},
    cores: [],
    // Challenge path mode owns this axis and intentionally wins over mode corePreset.
    settings: normalizeBogglePlaygroundSettings(
      { letterAdjacencyMode: getBoggleChallengePathModeAdjacency(configuration.pathMode) },
      getBoggleGameModeDefinition(gameModeId).corePreset,
    ),
    specialTileReveals: [],
    vowelRatioBand: null,
  }
  let rngState = initialRngState

  for (const step of configuration.steps) {
    const context = {
      configurationId: configuration.id,
      bonuses: rolledState.bonuses,
      gameModeId,
      rolledState,
    } satisfies BoggleChallengeConfigurationStepContext

    if (step.kind === 'core') {
      const options = step.resolveOptions(
        context,
      ) as readonly BoggleChallengeWeightedStepOption<BoggleChallengeCoreValue | null>[]

      // Empty weighted option lists intentionally skip this step without consuming RNG.
      if (options.length === 0) {
        continue
      }

      const pickedOption = pickWeightedEntry(options, rngState)

      if (pickedOption === null) {
        throw new Error(
          `No weighted configured core values available for ${configuration.id}:${step.definitionId}`,
        )
      }

      if (pickedOption.entry.value === null) {
        rngState = pickedOption.rngState
        continue
      }

      const resolved = resolveBoggleChallengeCoreValue(
        gameModeId,
        step.definitionId,
        pickedOption.entry.value,
        rolledState.settings,
      )
      rolledState.coreValuesByDefinitionId = {
        ...rolledState.coreValuesByDefinitionId,
        [step.definitionId]: pickedOption.entry.value,
      }
      rolledState.cores = [...rolledState.cores, resolved.core]
      rolledState.settings = resolved.settings
      rngState = pickedOption.rngState
      continue
    }

    if (step.kind === 'bonuses') {
      const bonusOptions = step.resolveOptions(context)

      // Empty weighted option lists intentionally skip this step without consuming RNG.
      if (bonusOptions.length === 0) {
        continue
      }

      const resolvedBonuses = resolveBoggleChallengeBonusSuiteFromRngState(
        bonusOptions,
        rngState,
        collectResolvedChallengeModifiers(rolledState),
      )

      if (resolvedBonuses === null) {
        throw new Error(`Could not resolve bonus suite for ${configuration.id}`)
      }

      rolledState.bonuses = [...rolledState.bonuses, ...resolvedBonuses.bonuses]
      rngState = resolvedBonuses.rngState
      continue
    }

    if (step.kind === 'board-distribution') {
      const boardOptions = step.resolveOptions(context)

      // Empty weighted option lists intentionally skip this step without consuming RNG.
      if (boardOptions.length === 0) {
        continue
      }

      const resolvedBoardDistribution = resolveBoggleChallengeBoardDistributionFromRngState(
        boardOptions,
        rngState,
        rolledState.bonuses,
      )

      if (resolvedBoardDistribution === null) {
        throw new Error(`Could not resolve board distribution for ${configuration.id}`)
      }

      if (resolvedBoardDistribution.mod === null) {
        rngState = resolvedBoardDistribution.rngState
        continue
      }

      rolledState.boardDistribution = resolvedBoardDistribution.mod
      rngState = resolvedBoardDistribution.rngState
      continue
    }

    if (step.kind === 'vowel-ratio-band') {
      const bandOptions = step.resolveOptions(context)

      if (bandOptions.length === 0) {
        continue
      }

      const pickedBand = pickWeightedEntry(bandOptions, rngState)
      if (pickedBand === null) {
        throw new Error(
          `No weighted vowel-ratio-band options available for ${configuration.id}:${step.id}`,
        )
      }

      rngState = pickedBand.rngState
      const pickedValue = pickedBand.entry.value
      if (pickedValue === null) {
        rolledState.vowelRatioBand = null
      } else {
        assertValidBoggleChallengeVowelRatioBand(pickedValue)
        rolledState.vowelRatioBand = pickedValue
      }
      continue
    }

    const settingsBeforeSpecialTiles = applyResolvedChallengeEffects(rolledState.settings, {
      bonuses: rolledState.bonuses,
      boardDistribution: rolledState.boardDistribution,
    })
    const rolledSpecialTiles = rollBoggleChallengeSpecialTiles(
      settingsBeforeSpecialTiles,
      rngState,
      step.resolveEligibility({
        configurationId: configuration.id,
        bonuses: rolledState.bonuses,
        gameModeId,
        rolledState,
      }),
    )

    rolledState.settings = rolledSpecialTiles.settings
    rolledState.specialTileReveals = rolledSpecialTiles.revealEntries
    rngState = rolledSpecialTiles.rngState

    const scoreThresholds = configuration.resolveScoreThresholds({
      boardDistribution: rolledState.boardDistribution,
      bonuses: rolledState.bonuses,
      configurationId: configuration.id,
      cores: rolledState.cores,
      gameModeId,
      pathMode: configuration.pathMode,
      settings: rolledState.settings,
      specialTileReveals: rolledState.specialTileReveals,
    })

    return {
      bonuses: rolledState.bonuses,
      boardDistribution: rolledState.boardDistribution,
      configurationId: configuration.id,
      pathMode: configuration.pathMode,
      rolledCoreState: {
        cores: rolledState.cores,
        rngState,
        settings: rolledState.settings,
        specialTileReveals: rolledState.specialTileReveals,
      },
      scoreThresholds,
      vowelRatioBand: rolledState.vowelRatioBand,
    }
  }

  throw new Error(
    `Configured challenge ${configuration.id} has no special-tile terminal step to finish resolution`,
  )
}

export function toBoggleResolvedChallengeRun(
  gameModeId: BoggleGameModeId,
  resolved: NonNullable<ReturnType<typeof resolveBoggleChallengeConfiguredSeedState>>,
): BoggleResolvedChallengeRun {
  const challenge = {
    bonuses: resolved.bonuses,
    boardDistribution: resolved.boardDistribution,
    vowelRatioBand: resolved.vowelRatioBand ?? null,
  }

  return {
    aceScore: resolved.scoreThresholds.aceScore,
    bonuses: resolved.bonuses,
    boardDistribution: resolved.boardDistribution,
    configurationId: resolved.configurationId,
    cores: resolved.rolledCoreState.cores,
    gameplay: buildBoggleResolvedChallengeGameplay(challenge),
    pathMode: resolved.pathMode,
    settings: resolved.rolledCoreState.settings,
    specialTileReveals: resolved.rolledCoreState.specialTileReveals,
    targetScore: resolved.scoreThresholds.targetScore,
  }
}

export function resolveRequiredBoggleChallengeConfiguredSeedState(
  gameModeId: BoggleGameModeId,
  seed: number | string,
) {
  const resolved = resolveBoggleChallengeConfiguredSeedState(gameModeId, seed)
  if (resolved) {
    return resolved
  }

  throw new Error(`No authored challenge configurations available for ${gameModeId}`)
}
